import { useContext, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { PlayerContext } from 'containers/Player/context/PlayerContext';
import { selectIsLiveChannel } from 'containers/Player/selectors/media.selector';
import { useVodPaywayToken } from 'containers/VodPlayer/hooks/useVodPaywayToken';
import {
  attachdevice,
  trackingEvent,
} from 'containers/Player/actions/tracking/tracking.actions';
import { getTickLanguageParams } from '../utils/languagePreferences';

const useTracking = (deviceId, groupId) => {
  const dispatch = useDispatch();
  const { bitmovinPlayerRef } = useContext(PlayerContext) || {};

  const pbi = useSelector(state => state?.Player?.PBI?.data);
  const isLive = useSelector(selectIsLiveChannel(groupId));
  const media = useSelector(state => {
    if (!state?.Player?.PictureInPicture?.showing) {
      return state?.Player?.Player?.data;
    } else {
      return state?.Player?.PictureInPicture?.media;
    }
  });
  const recordingSelected = useSelector(
    store => store?.Player?.RecordingSelected,
  );
  const currentAudioLanguage = useSelector(
    state => state.Player.OptionButtons.languageOptions.currentAudioLanguage,
  );

  const firstPlay = useRef(true);
  const initialTime = useRef(null);
  const tickInterval = useRef(null);
  const trackingData = useRef(null);

  const { playButton } = useVodPaywayToken(pbi);

  const handleAttach = () => {
    dispatch(
      attachdevice({
        group_id: media?.group?.common?.id,
        device_id: deviceId,
      }),
    );
  };

  const handleTick = (params = {}) => {
    dispatch(
      trackingEvent(media?.tracking?.urls?.tick, {
        timecode: getPlayingTime(),
        ...params,
      }),
    );
  };

  const handleStart = () => {
    const linealChannelData = JSON.parse(
      localStorage.getItem('linealChannelData') || '{}',
    );
    const found = linealChannelData?.find(
      paq =>
        paq?.groups?.indexOf(
          !recordingSelected
            ? media?.group?.common?.id
            : recordingSelected?.item?.id,
        ) > -1,
    );
    let purchaseid;
    if (found) purchaseid = found?.purchaseid;

    dispatch(
      trackingEvent(media?.tracking?.urls?.view, {
        purchase_id: playButton?.purchaseid || purchaseid || '',
        group_id: !recordingSelected
          ? media?.group?.common?.id
          : recordingSelected?.item?.id,
        device_id: deviceId,
      }),
    );
  };

  const handlePause = e => {
    if (
      bitmovinPlayerRef.current?.getCurrentTime() ===
      bitmovinPlayerRef.current?.getDuration()
    ) {
      return;
    }
    if (!isLive || recordingSelected) {
      dispatch(
        trackingEvent(media?.tracking?.urls?.pause, {
          timecode: getPlayingTime(),
        }),
      );
    }
  };

  const handleStop = time => {
    if (!media?.tracking?.urls?.stop) return;
    dispatch(
      trackingEvent(media?.tracking?.urls?.stop, {
        timecode: time ? time : getPlayingTime(),
      }),
    );
  };

  const handleResume = () => {
    if (firstPlay.current) {
      firstPlay.current = false;
      return;
    }
    if (!isLive || recordingSelected) {
      dispatch(
        trackingEvent(media?.tracking?.urls?.resume, {
          timecode: getPlayingTime(),
        }),
      );
    }
  };

  const handleError = () => {
    dispatch(
      trackingEvent(media?.tracking?.urls?.error, {
        timecode: getPlayingTime(),
      }),
    );
  };

  const handleSeek = () => {
    dispatch(
      trackingEvent(media?.tracking?.urls?.seek, {
        timecode: getPlayingTime(),
      }),
    );
  };

  const handleSubChange = (params = {}) => {
    dispatch(
      trackingEvent(media?.tracking?.urls?.dubsubchange, {
        timecode: getPlayingTime(),
        ...params,
      }),
    );
  };

  const handleCredits = () => {
    dispatch(
      trackingEvent(media?.tracking?.urls?.credits, {
        timecode: getPlayingTime(),
      }),
    );
  };

  const handleEnd = time => {
    if (!isLive) {
      dispatch(
        trackingEvent(trackingData?.current?.urls?.completion, {
          timecode: time ? time : getPlayingTime(),
        }),
      );
    }
  };

  const handleEpisodeChange = () => {
    if (isLive === false) {
      dispatch(
        trackingEvent(trackingData?.current?.urls?.episodechange, {
          timecode: getPlayingTime(),
        }),
      );
    }
  };

  useEffect(
    () => {
      if (!media || !isLive) return;
      handleAttach();
    },
    [media, isLive],
  );

  const getPlayingTime = () =>
    parseInt(bitmovinPlayerRef?.current?.getCurrentTime(), 10);

  const handleInitTracking = () => {
    if (!bitmovinPlayerRef.current) return;

    const updateTime = () => {
      if (!initialTime.current || initialTime?.current === 0) {
        initialTime.current = bitmovinPlayerRef.current?.getCurrentTime();
      }
    };

    bitmovinPlayerRef.current?.on(
      window.bitmovin.player.PlayerEvent.Ready,
      handleStart,
    );
    bitmovinPlayerRef.current?.on(
      window.bitmovin.player.PlayerEvent.Paused,
      handlePause,
    );
    bitmovinPlayerRef.current?.on(
      window.bitmovin.player.PlayerEvent.Play,
      handleResume,
    );
    bitmovinPlayerRef.current?.on(
      window.bitmovin.player.PlayerEvent.TimeChanged,
      updateTime,
    );
    bitmovinPlayerRef.current?.on(
      window.bitmovin.player.PlayerEvent.Error,
      handleError,
    );

    // eslint-disable-next-line consistent-return
    return () => {
      initialTime.current = 0;
      bitmovinPlayerRef.current?.off(
        window.bitmovin.player.PlayerEvent.Ready,
        handleStart,
      );
      bitmovinPlayerRef.current?.off(
        window.bitmovin.player.PlayerEvent.Paused,
        handlePause,
      );
      bitmovinPlayerRef.current?.off(
        window.bitmovin.player.PlayerEvent.Play,
        handleResume,
      );
      bitmovinPlayerRef.current?.off(
        window.bitmovin.player.PlayerEvent.TimeChanged,
        updateTime,
      );
      bitmovinPlayerRef.current?.off(
        window.bitmovin.player.PlayerEvent.Error,
        handleError,
      );
    };
  };

  useEffect(() => () => clearInterval(tickInterval?.current), []);

  useEffect(
    () => {
      if (!media) return;
      trackingData.current = media?.tracking;
    },
    [JSON.stringify(media), JSON.stringify(pbi)],
  );

  useEffect(
    () => {
      if (!media || !trackingData.current) return;

      clearInterval(tickInterval.current);

      tickInterval.current = setInterval(() => {
        handleTick(
          getTickLanguageParams({
            isLive,
            currentAudioLanguage,
          }),
        );
      }, trackingData.current?.policies?.tick_interval * 1000);

      return () => {
        clearInterval(tickInterval.current);
      };
    },
    [media?.group?.common?.id, pbi, currentAudioLanguage],
  );

  useEffect(
    () => {
      firstPlay.current = true;
    },
    [groupId],
  );

  return {
    handleInitTracking,
    handleStart,
    handlePause,
    handleTick,
    handleError,
    handleSeek,
    handleSubChange,
    handleCredits,
    handleEnd,
    handleStop,
    handleEpisodeChange,
    handleResume,
  };
};

export default useTracking;
